import {Injectable, model} from '@angular/core';
import {Observable} from "rxjs";
import {Configuration} from "../../assets/Configuration";
import {TrainingAnonymousModel} from "../models/training/training-anonymous.model";
import { HttpBackend, HttpClient, HttpParams } from "@angular/common/http";
import { RawFileModel } from '../models/raw-file-model';
import { AcceptTrainingAsGuestModel, AcceptTrainingWithInvitationModel } from '../models/training/accept-training-anonymous.model';
import { RegistrationFieldConfigModel } from '../models/registration-field-configs/registration-field-config.model';

@Injectable({
    providedIn: 'root'
})
export class TrainingAnonymousService {

    private httpClient: HttpClient;

    constructor(private httpBackend: HttpBackend) {
        this.httpClient = new HttpClient(httpBackend);
    }

    public getTrainingAnonymous(eventKey: string, accessToken: string | null): Observable<TrainingAnonymousModel> {
        let params = new HttpParams();

        if (accessToken) {
            params = params.set('token', accessToken);
        }
        
        return this.httpClient.get<TrainingAnonymousModel>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey,
            { params: params }
        );
    }

    public getIsAddedToCalendar(eventKey: string, token: string): Observable<boolean> {
        const params = new HttpParams().set('token', token);

        return this.httpClient.get<boolean>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/is-added-to-calendar',
            { params: params }
        );
    }

    public addToCalendar(eventKey: string, token: string): Observable<void> {
        const params = new HttpParams().set('token', token);

        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/add-to-calendar', 
            {},
            { params: params }
        );
    }

    public acceptTrainingAsGuest(
        eventKey: string, model: AcceptTrainingAsGuestModel
    ): Observable<string | null> {
        return this.httpClient.post<string | null>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/accept-as-guest', 
            model, 
            { responseType: 'text' as 'json' }
        );
    }

    public acceptTrainingWithInvitation(
        eventKey: string, model: AcceptTrainingWithInvitationModel
    ): Observable<string | null> {
        return this.httpClient.post<string | null>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/accept-with-token', 
            model, 
            { responseType: 'text' as 'json' }
        );
    }

    public declineTrainerInvitation(eventKey: string, accessToken: string): Observable<void> {
        const params = new HttpParams()
            .set('token', accessToken);

        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/trainer/decline',
            {},
            { params: params }
        );
    }

     public declineTraining(eventKey: string, accessToken: string): Observable<void> {
        const params = new HttpParams()
            .set('token', accessToken);

        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/decline',
            {},
            { params: params }
        );
    }

    public getTrainingAnonymousIcs(eventKey: string, accessToken: string | null): Observable<RawFileModel> {
        let params = new HttpParams();

        if (accessToken) {
            params = params.set('token', accessToken);
        }

        return this.httpClient.get<RawFileModel>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/ics',
            { params: params }
        );
    }

    public requestTrainingAnonymous(eventKey: string, model: AcceptTrainingAsGuestModel): Observable<void> {
        return this.httpClient.post<void>(Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/request', model);
    }

    public enqueueTrainingAsGuest(
        eventKey: string, model: AcceptTrainingAsGuestModel
    ): Observable<string | null> {
        return this.httpClient.post<string | null>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/enqueue-as-guest', 
            model, 
            { responseType: 'text' as 'json' }
        );
    }

    public enqueueTrainingWithInvitation(
        eventKey: string, model: AcceptTrainingWithInvitationModel
    ): Observable<string | null> {
        return this.httpClient.post<string | null>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/enqueue-with-token', 
            model, 
            { responseType: 'text' as 'json' }
        );
    }

    public dequeueTrainingAnonymous(eventKey: string, token: string): Observable<void> {
        return this.httpClient.post<void>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/dequeue', 
            { token: token }
        );
    }

    public getRegistrationFieldConfigs(eventKey: string): Observable<RegistrationFieldConfigModel[]> {
        return this.httpClient.get<RegistrationFieldConfigModel[]>(
            Configuration.getApiBaseUrl() + '/traininganonymous/' + eventKey + '/registration-field-configs'
        );
    }
}
